
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Check, Clock, Download, FileText, List, Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";

const WebsiteSeoSop = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>New Website SEO SOP | Los Angeles SEO Group</title>
        <meta
          name="description"
          content="Standard Operating Procedure (SOP) for optimizing a new website for search engines. A comprehensive guide from Los Angeles SEO Group."
        />
      </Helmet>

      <Navbar />

      <section className="py-12 md:py-20 bg-gradient-to-b from-muted/50 to-background">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
              <div>
                <Badge variant="outline" className="mb-4">Standard Operating Procedure</Badge>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  New Website <span className="text-gradient bg-sky-gradient">SEO SOP</span>
                </h1>
                <p className="text-lg text-foreground/70">
                  A comprehensive checklist and process to ensure your new website launches with 
                  strong SEO foundations. Follow these steps in order for optimal results.
                </p>
              </div>
              <div className="md:ml-auto mt-6 md:mt-0">
                <Button className="bg-desert-gradient hover:opacity-90 flex gap-2">
                  <Download className="h-4 w-4" />
                  <span>Download as PDF</span>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
              <Card className="bg-muted/50 col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    <span>Time Required</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/70">4-6 weeks</p>
                </CardContent>
              </Card>
              
              <Card className="bg-muted/50 col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <span>Difficulty</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/70">Intermediate</p>
                </CardContent>
              </Card>
              
              <Card className="bg-muted/50 md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <List className="h-5 w-5 text-primary" />
                    <span>Resources Needed</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/70">
                    Keyword research tools, analytics setup, access to website CMS/code
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-muted p-6 rounded-lg mb-12">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-lg mb-2">Important Note</h3>
                  <p className="text-foreground/70">
                    This SOP is designed for new websites, but can also be applied to redesigns or 
                    major website overhauls. For existing websites, ensure proper 301 redirects are 
                    implemented to maintain search equity.
                  </p>
                </div>
              </div>
            </div>

            <Accordion type="single" collapsible className="mb-12">
              <AccordionItem value="phase1">
                <AccordionTrigger className="text-xl font-semibold">
                  Phase 1: Pre-Launch Planning & Research
                </AccordionTrigger>
                <AccordionContent className="px-6">
                  <div className="space-y-6 py-4">
                    <div>
                      <h3 className="font-medium text-lg mb-2">1.1 Comprehensive Keyword Research</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Identify primary and secondary keywords for each page</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Analyze search intent for each keyword group</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Research long-tail keyword opportunities</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Document keyword mapping for site architecture</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">1.2 Competitor Analysis</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Identify top 5-10 competitors in search results</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Analyze their site structure and content strategy</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Review competitors' backlink profiles</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Identify content gaps and opportunities</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">1.3 Information Architecture Planning</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Design a search-friendly site structure</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Plan URL structure using keywords where appropriate</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Map content to user journey stages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Create navigation structure with SEO in mind</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="phase2">
                <AccordionTrigger className="text-xl font-semibold">
                  Phase 2: On-Page SEO Implementation
                </AccordionTrigger>
                <AccordionContent className="px-6">
                  <div className="space-y-6 py-4">
                    <div>
                      <h3 className="font-medium text-lg mb-2">2.1 Content Development</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Create comprehensive, high-quality content for each page</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Incorporate primary and secondary keywords naturally</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Ensure content addresses search intent</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Include semantic terms and related keywords</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">2.2 Technical On-Page Elements</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Optimize title tags with primary keywords (55-60 characters)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Write compelling meta descriptions (150-160 characters)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Implement proper heading structure (H1, H2, H3)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Optimize image alt text and filenames</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Create SEO-friendly URLs</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">2.3 Internal Linking Structure</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Create a logical internal linking structure</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Use descriptive, keyword-rich anchor text</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Link to important pages from homepage</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Implement breadcrumb navigation</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="phase3">
                <AccordionTrigger className="text-xl font-semibold">
                  Phase 3: Technical SEO Setup
                </AccordionTrigger>
                <AccordionContent className="px-6">
                  <div className="space-y-6 py-4">
                    <div>
                      <h3 className="font-medium text-lg mb-2">3.1 Website Performance Optimization</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Optimize image sizes and formats</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Minify CSS, JavaScript, and HTML</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Implement browser caching</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Enable GZIP compression</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Optimize for Core Web Vitals</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">3.2 Mobile Optimization</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Implement responsive design</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Test on various mobile devices</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Ensure tap targets are properly sized</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Check for mobile usability issues</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">3.3 Indexation Control</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Create and optimize robots.txt file</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Generate XML sitemap</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Implement canonical tags where needed</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Use meta robots tags appropriately</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">3.4 Structured Data Implementation</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Add organization schema</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Implement breadcrumb schema</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Add page-specific schema (Article, Product, FAQ, etc.)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Test schema implementation with Google's testing tool</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="phase4">
                <AccordionTrigger className="text-xl font-semibold">
                  Phase 4: Launch & Post-Launch Activities
                </AccordionTrigger>
                <AccordionContent className="px-6">
                  <div className="space-y-6 py-4">
                    <div>
                      <h3 className="font-medium text-lg mb-2">4.1 Pre-Launch Final Checks</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Conduct comprehensive technical SEO audit</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Check for broken links and 404 errors</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Verify all pages are accessible</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Test contact forms and conversion points</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Perform cross-browser testing</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">4.2 Analytics & Tracking Setup</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Install Google Analytics</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Set up Google Search Console</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Configure conversion tracking</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Set up custom dashboards and reports</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">4.3 Post-Launch Activities</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Submit XML sitemap to Google Search Console</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Request indexing of important pages</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Monitor indexation status</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Check for crawl errors and fix issues</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Begin initial link building activities</span>
                        </li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-lg mb-2">4.4 Ongoing Monitoring Plan</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Create monitoring schedule (daily, weekly, monthly tasks)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Set up alerts for significant changes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Establish KPIs and reporting framework</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span>Plan for content updates and optimizations</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Card className="mb-12">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5 text-primary" />
                  <span>SEO Tools Recommended for This Process</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium mb-2">Research & Planning</h3>
                    <ul className="space-y-1 text-foreground/70">
                      <li>• SEMrush or Ahrefs for keyword research</li>
                      <li>• Clearscope or SurferSEO for content optimization</li>
                      <li>• SpyFu for competitor analysis</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">Technical SEO</h3>
                    <ul className="space-y-1 text-foreground/70">
                      <li>• Screaming Frog for site audits</li>
                      <li>• GTmetrix for performance testing</li>
                      <li>• Schema App for structured data</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">Monitoring & Analytics</h3>
                    <ul className="space-y-1 text-foreground/70">
                      <li>• Google Analytics</li>
                      <li>• Google Search Console</li>
                      <li>• Databox for custom dashboards</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">Content & On-Page</h3>
                    <ul className="space-y-1 text-foreground/70">
                      <li>• Yoast SEO for WordPress sites</li>
                      <li>• Hemingway Editor for readability</li>
                      <li>• Grammarly for content quality</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-muted p-8 rounded-lg">
              <h2 className="text-2xl font-bold mb-4">Need Expert Help with Your Website SEO?</h2>
              <p className="text-lg text-foreground/70 mb-6">
                Our Los Angeles SEO team can implement this entire SOP for your business, 
                ensuring your website launches with a strong SEO foundation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-desert-gradient hover:opacity-90">
                  <Link to="/contact">Get a Free SEO Consultation</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/knowledge-base">Explore Our Knowledge Base</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </PageTransition>
  );
};

export default WebsiteSeoSop;
